// The vendored live engine's filename. hls.js is emitted into the dist beside the
// hashed main bundle by vite.main.config.mjs, and requested here by BARE RELATIVE
// NAME so the browser resolves it against the IFRAME DOCUMENT — the CDN
// (static.sh copies `dist/static/{*.js,*.js.map}` and futuri-post-widget.html
// into one flat dir) and test/e2e/serve.js both serve them from the same
// directory. This is the same convention the hashed main bundle already uses:
// emitIframeHtml injects its BARE name for exactly this reason.
//
// It replaces an ABSOLUTE S3 mirror URL uploaded by a `deploy_hls_assets` CI job
// gated to master, which meant the object did not exist on any other branch —
// window.Hls stayed undefined and attachLiveStream swallowed it (native-HLS
// branch on iOS Safari, `no-op` on Chrome/Firefox), so live audio was dead with
// no error. Vendoring removes the cross-origin fetch, the branch gating, and any
// chance of the engine version-skewing from the bundle that drives it.
//
// ⚠️ A BARE RELATIVE NAME IS PER-ORIGIN, AND PATH B NEEDS A VARNISH CHANGE.
// The absolute S3 URL had one virtue this does not: a single object served both
// deploy paths. Resolution now depends on the serving origin:
//   - Path A (the widget's own CloudFront, `make upload-static-dev` →
//     scripts/static.sh) — WORKS. static.sh's `dist/static/{*.js,*.js.map}` copy
//     flattens the engine beside futuri-post-widget.html on the CDN root.
//   - Path B (static-app-builder → the CLUSTER's Varnish, which is where real
//     customer embeds are served from — post_config.widget_domain MUST be the
//     cluster's own host so the widget is same-origin with the editor) —
//     **404s today.** Varnish only routes to the static-app S3 backend for an
//     explicit regex whitelist in create_env/cfg/varnish/v4.vcl.j2, and
//     `/hls.light.min.js` matches none of its alternatives; the `/static/`-
//     prefixed variant is intercepted earlier and sent to nginx-static.
//     Measured on uat 2026-07-27: /futuri-widget-main.js → 200 (AmazonS3),
//     /hls.light.min.js → 404 (gunicorn), /static/hls.light.min.js → 404 (nginx).
//     static-app-builder already uploads the object, so no builder change is
//     needed — the whitelist does. Until that lands + `deploy-varnish`, live
//     audio stays dead on every cluster embed exactly as before, and QA on
//     Path A (widgetv2-dev) will read as fixed. NOT a regression: Path B was
//     already broken by the missing S3 object.
//     Verify before any cluster deploy:
//       curl -I https://<cluster>.futurimedia.com/hls.light.min.js
//
// Duplicated (by design) in vite.main.config.mjs and scripts/verify-artifact-shape.js
// — a gate must state its expectation independently of the code it checks.
// test/unit/hls-vendored-asset.spec.js pins the three against drift.
export const HLS_ASSET_NAME = 'hls.light.min.js';

// Singleton loader for hls.js — the live engine (LP-H/MR-75). It is the direct
// replacement for the retired Viblast loader (removed in LP-4/MR-78): hls.js
// ships as a LOADED GLOBAL (window.Hls), NOT a bundled import, so the single IIFE
// bundle + its gzip budget stay unchanged (inlining hls.js into the bundle is a
// separate, still-deferred change). Injects the vendored sibling script into
// <head> async and captures window.Hls on load; the _enabled guard keeps it to
// exactly one injection.
class HlsLoader {
  constructor() {
    this._enabled = false;
    this.Hls = null;
  }

  // asynchronously download the hls.js code. Takes no args: the loader always
  // injects the fixed HLS_ASSET_NAME, so the old `load(code, sourceUrl)` params
  // (dead Viblast-era leftovers — `code` unread, `sourceUrl` stored to a
  // never-read `_sourceUrl`) are dropped. Removing them means a mis-call can no
  // longer slip an undefined arg through and have load() proceed on it (PS-19).
  load() {
    if (this._enabled) { return; }
    this._enabled = true;
    const el = document.createElement('script');
    el.type = 'text/javascript';
    el.async = true;
    el.onload = this._onLoad.bind(this);
    el.src = HLS_ASSET_NAME;
    document.getElementsByTagName('head')[0].appendChild(el);
  }

  // called when the script tag finishes downloading
  _onLoad() {
    this.Hls = window.Hls;
  }
}


export default new HlsLoader();
